import { ErrorMessage, SuccessMessage } from '@/components/Forms/styles';
import Web2CaseInput from '@/components/Forms/Web2CaseForm/components/input';
import ProductComplaintFields from '@/components/Forms/Web2CaseForm/components/productComplaintFields';
import Web2CaseSelect from '@/components/Forms/Web2CaseForm/components/select';
import Web2CaseTextArea from '@/components/Forms/Web2CaseForm/components/textarea';
import { LiquidButton } from '@/components/LiquidButton';
import { COLORS } from '@/styles/constants';
import { useEffect, useRef, useState } from 'react';
import { salutationConfig } from '@/components/Forms/Web2CaseForm/config/salutation';
import {
  CASE_VALUES,
  INBOUND_VALUES,
  TOPIC_VALUES,
  topicsConfig,
} from '@/components/Forms/Web2CaseForm/config/topics';
import { SF_INPUT_VALUE, SF_INPUT_ID } from '@/components/Forms/Web2CaseForm/config/sfInput';
import { stateConfig } from '@/components/Forms/Web2CaseForm/config/state';
import { createDescription, formDataToObject } from '@/components/Forms/Web2CaseForm/helper/form';
import { zipcodePatternConfig } from '@/components/Forms/Web2CaseForm/config/zipcode';
import { translationConfig } from '@/components/Forms/Web2CaseForm/config/translations';

type Props = {
  locale: string;
};

const Web2CaseForm = ({ locale }: Props) => {
  const [submitStatus, setSubmitStatus] = useState<undefined | 'pending' | 'success' | 'error'>(
    undefined,
  );
  const [showProductComplaintInput, setShowProductComplaintInput] = useState(false);
  const [showProductNameInput, setShowProductNameInput] = useState(false);
  const msgRef = useRef<HTMLDivElement | null>(null);

  const translations = translationConfig[locale];

  useEffect(() => {
    if (submitStatus === 'error' || submitStatus === 'success') {
      msgRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [submitStatus]);

  const handleSubmit = async (formData: FormData) => {
    setSubmitStatus('pending');

    const topicValue = formData.get('topic');
    const topic = topicsConfig[locale].find((item) => item.value === topicValue);
    const inputValue = SF_INPUT_VALUE[locale];

    const formJson = formDataToObject(formData, ['topic', 'case', 'product_name', 'description']);

    const res = await fetch('/next-api/web2case', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify({
        [SF_INPUT_ID.INBOUND]: topic?.inbound ?? INBOUND_VALUES.GENERAL,
        [SF_INPUT_ID.BRAND]: 'Dr. Oetker',
        [SF_INPUT_ID.COUNTRY]: inputValue.COUNTRY,
        [SF_INPUT_ID.PREFERED_LANG]: inputValue.PREFERED_LANG,
        [SF_INPUT_ID.ADDRESS_COUNTRY]: inputValue.ADDRESS_COUNTRY,
        case: topic?.case ?? CASE_VALUES.OTHER,
        external: '1',
        retURL: inputValue.RET_URL,
        subject: `${inputValue.SUBJECT_PREFIX} - ${topic?.label ?? INBOUND_VALUES.GENERAL}`,
        status: 'Open',
        description: createDescription(formData),
        ...formJson,
      }),
    });

    if (!res.ok) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('success');
  };

  const onTopicChange = (value: string) => {
    setShowProductComplaintInput(false);
    setShowProductNameInput(false);

    if (value === TOPIC_VALUES.PRODUCT_CRITICISM) {
      setShowProductComplaintInput(true);
    }

    if (value === TOPIC_VALUES.PRODUCT_SEARCH) {
      setShowProductNameInput(true);
    }
  };

  const stateOptions = [{ label: translations.labelInitialStateOption, value: '' }].concat(
    stateConfig[locale].map((item) => ({ value: item.code, label: item.state })),
  );
  const topicOptions = [{ value: '', label: translations.labelInitialTopicOption }].concat(
    topicsConfig[locale].map((item) => ({ value: item.value, label: item.label })),
  );
  const salutationOptions = [
    { value: '', label: translations.labelInitialSalutationOption },
  ].concat(salutationConfig[locale]);

  return (
    <div>
      <form className="flex flex-col gap-8 mb-5" action={handleSubmit}>
        <Web2CaseSelect
          onChange={onTopicChange}
          label={translations.labelTopic}
          name="topic"
          options={topicOptions}
        />

        {showProductComplaintInput && (
          <ProductComplaintFields
            productLabel={translations.labelProduct}
            lotLabel={translations.labelLot}
            codeLabel={translations.labelCode}
          />
        )}

        {showProductNameInput && (
          <Web2CaseInput
            label={translations.labelProduct}
            name="product_name"
            maxLength={80}
            required
          />
        )}

        <Web2CaseTextArea name="description" label={translations.labelDescription} />

        <Web2CaseSelect
          name={SF_INPUT_ID.SALUTATION}
          title="Salutation"
          label={translations.labelSalutation}
          options={salutationOptions}
        />
        <div className="flex space-x-5">
          <Web2CaseInput
            label={translations.labelFirstname}
            name={SF_INPUT_ID.FIRSTNAME}
            maxLength={80}
            required
          />
          <Web2CaseInput
            label={translations.labelLastname}
            name={SF_INPUT_ID.LASTNAME}
            maxLength={80}
            required
          />
        </div>

        <Web2CaseInput
          label={translations.labelStreet}
          maxLength={255}
          name={SF_INPUT_ID.STREET}
          type="text"
        />
        <div className="flex space-x-5">
          <Web2CaseInput
            label={translations.labelZip}
            maxLength={255}
            name={SF_INPUT_ID.ZIP}
            type="text"
            className="w-1/3"
            pattern={zipcodePatternConfig[locale]}
          />
          <Web2CaseInput
            label={translations.labelCity}
            maxLength={255}
            name={SF_INPUT_ID.CITY}
            type="text"
            className="w-2/3"
          />
        </div>
        <Web2CaseSelect name="state" label={translations.labelState} options={stateOptions} />

        <Web2CaseInput
          label={translations.labelEmail}
          name="email"
          type="email"
          maxLength={80}
          required
        />
        <Web2CaseInput label={translations.labelPhone} name="phone" maxLength={40} />

        <label className="flex items-center gap-5">
          <input type="checkbox" required id="data_security_consent" name="data_security_consent" />
          {translations.consent}
        </label>

        <LiquidButton
          text={translations.labelSubmit}
          type="submit"
          as="button"
          isWidthAuto
          height={50}
          textColor={COLORS.white}
          color={COLORS.primary}
          disabled={submitStatus === 'pending'}
        />
      </form>

      <p>{translations.mandatoryInfo}</p>

      <div className="text-center" ref={msgRef}>
        {submitStatus === 'success' && (
          <SuccessMessage tag="p" type="md">
            {translations.successMsg}
          </SuccessMessage>
        )}
        {submitStatus === 'error' && (
          <ErrorMessage as="p" className="text-xl">
            {translations.errorMsg}
          </ErrorMessage>
        )}
      </div>
    </div>
  );
};

export default Web2CaseForm;
